import React, { useEffect, useRef } from "react";
import Acumen_25_Years_LOGO from "../assets/Acumen_25_Years_LOGO.png";

const T = {
  navy: "#A80D2C",
  blue: "#C4163A",
  teal: "#7B0F2B",
  gold: "#D79A1E",
  slate: "#5B677A",
  bg: "#F5F8FC",
  card: "#FFFFFF",
  line: "#DCE5EF",
  ink: "#1D2733",
  red: "#A31621",
  redDark: "#7A0F18",
  acNavy: "#11304F",
  green: "#2E8B57",
  greenDark: "#1F6B3F",
  grayBg: "#F2F2F2",
};

/**
 * HiddenT90Report
 * ────────────────
 * Renders the Transition 90 report offscreen at a fixed size (mirroring
 * HiddenVAKReport.jsx's approach exactly), captures each .t90-page with
 * html2canvas, stitches into a multi-page A4 PDF, then saves it.
 *
 * Props:
 * participantData  – null | { respondent, overall, mindset, skill, radar, submission_time }
 *                     (this is the `data` object from POST /calculate-score/t90,
 *                     spread with `submission_time` — same shape T90Report.jsx
 *                     builds in handleDownloadPDF)
 * onComplete       – () => void
 */
export default function HiddenT90Report({ participantData, onComplete }) {
  const containerRef = useRef(null);
  const hasRun = useRef(false); // guard against StrictMode double-invoke

  // ── Load html2canvas + jsPDF once ──────────────────────────────────────
  useEffect(() => {
    let pending = 2;
    const done = () => { pending--; };
    const load = (src, id) => {
      if (document.getElementById(id)) { done(); return; }
      const s = document.createElement("script");
      s.id = id; s.src = src;
      s.onload = done; s.onerror = done;
      document.head.appendChild(s);
    };
    load("https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js", "h2c-t90-v1");
    load("https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js", "jspdf-t90-v1");
  }, []);

  // ── Generate PDF whenever participantData is set ────────────────────────
  useEffect(() => {
    if (!participantData) return;
    if (hasRun.current) return;
    hasRun.current = true;

    const generate = async () => {
      await new Promise((res) => {
        const check = () => {
          if (window.html2canvas && window.jspdf?.jsPDF) { res(); return; }
          setTimeout(check, 100);
        };
        check();
      });

      await new Promise((r) => setTimeout(r, 1000));

      const el = containerRef.current;
      if (!el) { hasRun.current = false; onComplete?.(); return; }

      try {
        const { jsPDF } = window.jspdf;
        const A4_W = 210;
        const A4_H = 297;

        const pageEls = Array.from(el.querySelectorAll(".t90-page"));
        const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

        for (let i = 0; i < pageEls.length; i++) {
          const canvas = await window.html2canvas(pageEls[i], {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            logging: false,
            backgroundColor: "#ffffff",
            width: pageEls[i].scrollWidth,
            height: pageEls[i].scrollHeight,
            windowWidth: pageEls[i].scrollWidth,
          });

          const imgData = canvas.toDataURL("image/jpeg", 0.95);
          const logicalW = canvas.width / 2;
          const logicalH = canvas.height / 2;
          const MM_PER_PX = 25.4 / 96;
          const naturalW = logicalW * MM_PER_PX;
          const naturalH = logicalH * MM_PER_PX;
          const scaleToW = A4_W / naturalW;
          const projectedH = naturalH * scaleToW;

          let imgW, imgH, xOff, yOff;
          if (projectedH <= A4_H) {
            imgW = A4_W; imgH = projectedH; xOff = 0; yOff = 0;
          } else {
            const scaleToH = A4_H / naturalH;
            imgW = naturalW * scaleToH; imgH = A4_H;
            xOff = (A4_W - imgW) / 2; yOff = 0;
          }

          if (i > 0) pdf.addPage();
          pdf.addImage(imgData, "JPEG", xOff, yOff, imgW, imgH);
        }

        const safeName = (participantData.respondent?.name || "Report").replace(/\s+/g, "_");
        pdf.save(`Transition90_Report_${safeName}.pdf`);
      } catch (err) {
        console.error("Transition 90 PDF generation failed", err);
      } finally {
        hasRun.current = false;
        onComplete?.();
      }
    };

    generate();
  }, [participantData]);

  if (!participantData) return null;

  const {
    respondent = {},
    overall = { pct: 0 },
    mindset = { pct: 0, dimensions: [] },
    skill = { pct: 0, dimensions: [] },
    radar = { mindset: { codes: [], values: [] }, skill: { codes: [], values: [] } },
    submission_time,
  } = participantData;

  const dateStr = submission_time
    ? new Date(submission_time).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })
    : new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  // v4 — scoring bands: 0–39 / 40–59 / 60–79 / 80–100%
  const bandInfo = (pct) => {
    if (pct >= 80) return { level: "strong", label: "Core Strength" };
    if (pct >= 60) return { level: "estab", label: "Established Strength" };
    if (pct >= 40) return { level: "emerging", label: "Emerging Strength" };
    return { level: "dev", label: "Development Priority" };
  };

  const readinessNarrative = (overallPct, mindsetPct, skillPct) => {
    const gap = skillPct - mindsetPct;
    const overallBand = bandInfo(overallPct).label.toLowerCase();

    if (Math.abs(gap) < 8) {
      return `This is a balanced profile — the belief system and the practical judgement are developing in step with each other, which is the healthiest pattern to see at this stage. Neither score is running ahead of or masking the other, so there's no hidden gap for pressure to expose later. Overall readiness sits at ${overallPct}% (${overallBand}), and coaching time is best spent lifting both layers together on the specific dimensions flagged below, rather than favouring one over the other.`;
    }

    if (gap >= 8) {
      return `This is a solidifying profile, with the practical judgement running ahead of an already-developing belief system — this participant is executing well above what the mindset score alone would predict. That's a strong signal for readiness: the behaviour is consistent, and the belief system underneath it — while not yet at the same level — is what's worth reinforcing next. Overall readiness sits at ${overallPct}% (${overallBand}), and coaching time is better spent closing the specific remaining mindset gaps below than on foundational rebuilding.`;
    }

    return `This is a profile where the belief system is running ahead of the practical judgement — the underlying mindsets are more developed than the situational skill scores currently show. That's a useful distinction: this isn't a belief problem, it's an execution gap, which usually closes faster with structured practice and real-world reps than with more conceptual coaching. Overall readiness sits at ${overallPct}% (${overallBand}), and the fastest path forward is deliberate practice on the specific skill dimensions flagged below.`;
  };

  const chipColor = (level) => {
    switch (level) {
      case "strong":   return { bg: "#dcf0e3", fg: "#155c31" };
      case "estab":    return { bg: "#e3eaf5", fg: "#3a5a8a" };
      case "emerging": return { bg: "#fdf1da", fg: "#b8730f" };
      default:         return { bg: "#fbe6e6", fg: "#A31621" };
    }
  };

  // Fixed-size SVG radar (same math as the on-screen version) — fixed px
  // rather than responsive, matching how the rest of this hidden root uses
  // fixed sizing so html2canvas captures it identically every time.
  const Radar = ({ series, size = 720 }) => {
    const top = series[0] || { codes: [], values: [] };
    const bottom = series[1] || { codes: [], values: [] };
    const M = top.codes.length;
    const S = bottom.codes.length;
    if (!M && !S) return null;
    const cx = size / 2, cy = size / 2;
    const R = size / 2 - 95;
    const toRad = (deg) => (deg * Math.PI) / 180;
    const pointAt = (angleDeg, val) => {
      const r = (Math.max(0, Math.min(100, val)) / 100) * R;
      const a = toRad(angleDeg);
      return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
    };
    const pointAtFrac = (angleDeg, frac) => {
      const r = frac * R;
      const a = toRad(angleDeg);
      return [cx + r * Math.cos(a), cy - r * Math.sin(a)];
    };
    // Single continuous 360° wheel: M1 sits at the top and the M axes run
    // clockwise, then the S axes continue clockwise around the rest of the
    // circle back to just before M1 — matching the reference chart.
    const N = M + S;
    const unit = N ? 360 / N : 0;
    const topAngle = (i) => 90 - i * unit;
    const bottomAngle = (i) => 90 - (M + i) * unit;
    const ringLevels = [20, 40, 60, 80, 100];
    const topPts = top.values.map((v, i) => pointAt(topAngle(i), v));
    const bottomPts = bottom.values.map((v, i) => pointAt(bottomAngle(i), v));

    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Dimension radar chart">
        {ringLevels.map((lvl) => (
          <circle
            key={lvl}
            cx={cx}
            cy={cy}
            r={(lvl / 100) * R}
            fill="none"
            stroke={lvl === 100 ? "#8a94a3" : "#e2e8f0"}
            strokeWidth={lvl === 100 ? "2.2" : "1.6"}
          />
        ))}
        {top.codes.map((code, i) => {
          const a = topAngle(i);
          const [x, y] = pointAtFrac(a, 1.03);
          const [lx, ly] = pointAtFrac(a, 1.14);
          return (
            <g key={`t-${code}`}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke="#c7ccd4" strokeWidth="1.4" />
              <text x={lx} y={ly} fontSize="19" fill="#A31621" textAnchor="middle" dominantBaseline="middle">{code}</text>
            </g>
          );
        })}
        {bottom.codes.map((code, i) => {
          const a = bottomAngle(i);
          const [x, y] = pointAtFrac(a, 1.03);
          const [lx, ly] = pointAtFrac(a, 1.14);
          return (
            <g key={`b-${code}`}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke="#c7ccd4" strokeWidth="1.4" />
              <text x={lx} y={ly} fontSize="19" fill="#F5A623" textAnchor="middle" dominantBaseline="middle">{displayCode(code)}</text>
            </g>
          );
        })}
        {/* Bold radial axis line carrying the 0/20/40/60/80/100 ticks, running up the M1 axis at the top, matching the Plotly reference */}
        <line x1={cx} y1={cy} x2={cx} y2={cy - R} stroke="#4b5563" strokeWidth="1.6" />
        {[0, 20, 40, 60, 80, 100].map((v) => {
          const [x, y] = pointAt(90, v);
          return (
            <text key={v} x={x + 10} y={y} fontSize="18" fill="#8a94a3" textAnchor="start" dominantBaseline="middle">{v}</text>
          );
        })}
        {M > 0 && (
          <polygon points={topPts.map((p) => p.join(',')).join(' ')} fill={top.fill} stroke={top.stroke} strokeWidth="2.6" />
        )}
        {S > 0 && (
          <polygon points={bottomPts.map((p) => p.join(',')).join(' ')} fill={bottom.fill} stroke={bottom.stroke} strokeWidth="2.6" />
        )}
        {topPts.map((p, i) => (
          <circle key={`td-${i}`} cx={p[0]} cy={p[1]} r="4.5" fill={top.stroke} />
        ))}
        {bottomPts.map((p, i) => (
          <circle key={`bd-${i}`} cx={p[0]} cy={p[1]} r="4.5" fill={bottom.stroke} />
        ))}
      </svg>
    );
  };

  // Fixed-size SVG donut for the Overall Readiness score, mirroring the
  // gradient ring used in the HTML reference.
  const Donut = ({ pct, size = 220 }) => {
    const r = 92;
    const circumference = 2 * Math.PI * r;
    const offset = circumference - (Math.max(0, Math.min(100, pct)) / 100) * circumference;
    const cx = size / 2, cy = size / 2;
    return (
      <div className="donut-wrap">
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={cx} cy={cy} r={r} fill="none" stroke="#eee" strokeWidth="20" />
          <circle
            cx={cx} cy={cy} r={r} fill="none" stroke="url(#gradReadiness)"
            strokeWidth="20" strokeLinecap="round"
            strokeDasharray={circumference.toFixed(1)}
            strokeDashoffset={offset.toFixed(1)}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
          <defs>
            <linearGradient id="gradReadiness" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#A31621" />
              <stop offset="100%" stopColor="#F5A623" />
            </linearGradient>
          </defs>
        </svg>
        <div className="donut-center">
          <div className="pct">{pct}%</div>
          <div className="lbl">Overall Readiness</div>
        </div>
      </div>
    );
  };

  // Display-only relabeling of Skill IDs. This ONLY changes what is shown on
  // screen — d.code itself (used for keys, scoring, and internal lookups)
  // is never touched.
  const SKILL_ID_DISPLAY_MAP = {
    S5: "S4",
    S6: "S5",
    S7: "S6",
    S9: "S7",
    S10: "S8",
    S11: "S9",
    S12: "S10",
    S13: "S11",
    S14: "S12",
  };
  const displayCode = (code) => {
    if (SKILL_ID_DISPLAY_MAP[code]) return SKILL_ID_DISPLAY_MAP[code];
    return code;
  };

  const renderDimensionTable = (dims) => (
    <div className="dim-table">
      {dims.map((d) => {
        const c = chipColor(d.interpretation.level);
        return (
          <div className="dim-row" key={d.code}>
            <div className="dim-name">
              <strong>{d.label}</strong>
              <span>{displayCode(d.code)}</span>
            </div>
            <div className="dim-bar-wrap">
              <div className="dim-bar-track"><div className="dim-bar-fill" style={{ width: `${d.pct}%` }} /></div>
              <span className="dim-pct">{d.pct}%</span>
            </div>
            <div className="dim-chip" style={{ background: c.bg, color: c.fg }}>{d.interpretation.label}</div>
          </div>
        );
      })}
    </div>
  );

  // ── v2 — Mindset ↔ Skill map + Strengths/Priorities synthesis helpers ──
  const MINDSET_SKILL_MAP = [
    { m: "M1",  mLabel: "Letting Go",                              s: ["S1"],       sLabel: "S1 Delegation",                    why: "Letting go is the belief; delegation is the behaviour it enables" },
    { m: "M2",  mLabel: "Handling Insecurities",                   s: ["S2","S14"],  sLabel: "S2 Feedback, S12 Influencing",      why: "Both require staying non-defensive while exposed to judgement" },
    { m: "M3",  mLabel: "Creating Boundaries",                     s: ["S6"],       sLabel: "S5 Setting Priorities",            why: "Boundaries are what make real prioritisation stick" },
    { m: "M4",  mLabel: "Managing Power Awkwardness",               s: ["S3"],       sLabel: "S3 Building Trust",                why: "Both concern navigating an uneven power dynamic honestly" },
    { m: "M5",  mLabel: "Comfort with Ambiguity",                  s: ["S7"],       sLabel: "S6 Team Planning",                 why: "Planning under incomplete information requires tolerating ambiguity" },
    { m: "M6",  mLabel: "Thinking in Outcomes",                    s: ["S11"],       sLabel: "S9 Monitoring Performance",        why: "Reviewing outcomes requires already thinking in outcome terms" },
    { m: "M7",  mLabel: "Pride in Others Surpassing You",          s: ["S9","S10"],  sLabel: "S7 Spotting Talent, S8 Coaching",  why: "Both require genuinely wanting others to outgrow you" },
    { m: "M8",  mLabel: "Shifting from 'I' to 'We'",                s: ["S12"],      sLabel: "S10 Team Building",                why: "Team-building is impossible while still thinking individually" },
    { m: "M9",  mLabel: "Trusting Team Over Controlling Output",   s: ["S9","S10"],  sLabel: "S7 Spotting Talent, S8 Coaching",  why: "Trust is the precondition for developing others" },
    { m: "M10", mLabel: "Holding People Accountable",              s: ["S11"],       sLabel: "S9 Monitoring Performance",        why: "Accountability requires the discipline to track and follow up" },
    { m: "M11", mLabel: "Seeing the Org as a System",               s: ["S13","S14"], sLabel: "S11 Managing Stakeholders, S12 Influencing", why: "Systems-thinking is what stakeholder and influence work runs on" },
    { m: "M12", mLabel: "Believing in Self",                       s: ["S5"],      sLabel: "S4 Being Assertive",              why: "Self-belief is the prerequisite for holding a position under pressure" },
  ];

  const joinList = (items) => {
    if (items.length === 0) return "";
    if (items.length === 1) return items[0];
    if (items.length === 2) return `${items[0]} and ${items[1]}`;
    return `${items.slice(0, -1).join(", ")}, and ${items[items.length - 1]}`;
  };

  const mindsetDims = mindset.dimensions || [];
  const skillDims = skill.dimensions || [];

  const coreStrengthLabel = (d) => `${d.label} (${d.pct}%)`;
  const mindsetCoreStrengths = mindsetDims.filter((d) => d.pct >= 80);
  const skillCoreStrengths = skillDims.filter((d) => d.pct >= 80);

  const lowestMindset = mindsetDims.length
    ? mindsetDims.reduce((min, d) => (d.pct < min.pct ? d : min), mindsetDims[0])
    : null;
  const lowestSkillPct = skillDims.length ? Math.min(...skillDims.map((d) => d.pct)) : null;
  const lowestSkills = skillDims.filter((d) => d.pct === lowestSkillPct);

  const linkedRow = lowestMindset ? MINDSET_SKILL_MAP.find((r) => r.m === lowestMindset.code) : null;
  const linkedSkillDims = linkedRow ? skillDims.filter((d) => linkedRow.s.includes(d.code)) : [];
  const isLinkedAlsoLowestSkill =
    linkedSkillDims.length > 0 &&
    lowestSkillPct !== null &&
    linkedSkillDims.every((d) => d.pct === lowestSkillPct);
  // Split linked skill dimensions into weak (<50%, i.e. below the midpoint)
  // and strong (>=50%) groups. A mindset dimension can link to MORE THAN ONE
  // skill dimension (e.g. M2 -> S2, S3), and those can land on opposite
  // sides of the midpoint — so we can't just check "all" or "none" without
  // misdescribing the mixed case.
  const weakLinkedSkillDims = linkedSkillDims.filter((d) => d.pct < 50);
  const strongLinkedSkillDims = linkedSkillDims.filter((d) => d.pct >= 50);
  const linkedBelowMidpoint = linkedSkillDims.length > 0 && strongLinkedSkillDims.length === 0;
  const linkedShowsMatchingGap = weakLinkedSkillDims.length > 0;
  const secondaryPriorities = skillDims.filter(
    (d) => d.pct < 40 && !linkedSkillDims.some((ld) => ld.code === d.code)
  );

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: "fixed",
        top: "0",
        left: "-9999px",
        width: "1100px",
        opacity: 0,
        pointerEvents: "none",
        zIndex: -1,
      }}
    >
      <link href="https://api.fontshare.com/v2/css?f[]=satoshi@300,400,500,700,900&display=swap" rel="stylesheet" />

      <style>{`
        .t90-pdf-root {
          font-family: 'Satoshi', sans-serif;
          background: linear-gradient(180deg,#eef4f9 0%,#f7fafc 100%);
          color: #1f2937;
          line-height: 1.5;
        }
        .t90-pdf-root .report { max-width: 1100px; margin: 0 auto; padding: 2rem 1rem 4rem; }
        .t90-pdf-root .t90-page {
          background: #ffffff; border: 1px solid #d9e0e7; border-radius: 28px;
          box-shadow: 0 18px 40px rgba(14,39,68,.10); overflow: hidden; margin-bottom: 2rem;
        }

        /* Cover */
        .t90-pdf-root .cover { min-height:1556px; position:relative; color:white; }
        .t90-pdf-root .cover-inner { padding:3.5rem; display:flex; flex-direction:column; justify-content:space-between; min-height:1556px; }
        .t90-pdf-root .brand { display:flex; align-items:center; justify-content:space-between; gap:1rem; position:relative; z-index: 2; }
        .t90-pdf-root .badge { border:1px solid rgba(255,255,255,.2); background:rgba(255,255,255,.1); padding:.5rem 1rem; border-radius:999px; font-size:0.76rem; }

        .t90-pdf-root .logo-panel {
          position: absolute;
          top: 0;
          right: 0;
          width: 500px;
          height: 250px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1;
        }
        .t90-pdf-root .logo-panel-inner {
          display: flex;
          flex-direction: column;
          align-items: center;
          position: relative;
          z-index: 2;
          padding-left: 60px;
          padding-bottom: 25px;
        }
        .t90-pdf-root .logo-img { height: 65px; width: auto; object-fit: contain; flex-shrink: 0; }

        .t90-pdf-root .cover-title h2 { margin:0 0 1rem; font-size:3.4rem; line-height:.95; color:#FFFFFF; white-space:nowrap; max-width:none; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .t90-pdf-root .cover-title p  { font-size:1.2rem; color:rgba(255,255,255,.82); max-width:100%; text-align:justify; }
        .t90-pdf-root .cover-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:2.5rem; }
        .t90-pdf-root .meta { background:rgba(255,255,255,.1); font-size:1.2rem; border:1px solid rgba(255,255,255,.14); border-radius:16px; padding:.9rem 1rem; }
        .t90-pdf-root .meta span   { display:block; font-size:0.75rem; color:rgba(255,255,255,.65); margin-bottom:.2rem; }
        .t90-pdf-root .meta strong { font-size:0.95rem; color:#FFFFFF; }
        .t90-pdf-root .cover-badges { display:flex; flex-wrap:wrap; gap:.6rem; margin-top:2rem; }
        .t90-pdf-root .cover-badge  { background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.18); padding:.5rem .9rem; border-radius:999px; font-size:0.95rem; }
        .t90-pdf-root .footer-note { display:flex; justify-content:space-between; gap:1rem; color:rgba(255,255,255,.68); font-size:0.75rem; margin-top:1rem; }

        /* Inner pages */
        .t90-pdf-root .page-inner { padding: 2.5rem; }
        .t90-pdf-root .topbar { display:flex; justify-content:space-between; align-items:flex-start; gap:1rem; margin-bottom:2rem; }
        .t90-pdf-root .section-kicker { color:#A31621; font-size:0.8rem; text-transform:uppercase; letter-spacing:.12em; font-weight:700; }
        .t90-pdf-root .section-title { margin:.35rem 0 0; font-size:2rem; color:#11304F; border-left: 5px solid #A31621; padding-left: .75rem; }
        .t90-pdf-root .summary-grid { display:grid; grid-template-columns:1fr; gap:1rem; }
        .t90-pdf-root .card { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid #d9e0e7; border-radius:20px; padding:1.5rem; }
        .t90-pdf-root .card h3 { margin:0 0 .75rem; font-size:1.2rem; color:#A31621; }
        .t90-pdf-root .lead { color:#334155; font-size:1rem; line-height:1.75; margin:0; text-align:left; }
        .t90-pdf-root .pill { display:inline-flex; align-items:center; gap:.45rem; padding:.5rem .85rem; border-radius:999px; font-size:0.8rem; font-weight:700; text-transform:uppercase; letter-spacing:.06em; }
        .t90-pdf-root .pill.blue { background:#fef2f2; color:#A31621; }

        /* KPI row */
        .t90-pdf-root .kpi-row { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-bottom:1.5rem; }
        .t90-pdf-root .kpi { background:linear-gradient(180deg,#fff 0%,#fbfcfd 100%); border:1px solid #d9e0e7; border-radius:14px; padding:1.25rem; }
        .t90-pdf-root .kpi .lbl { font-size:0.78rem; text-transform:uppercase; letter-spacing:.07em; color:#6b7280; }
        .t90-pdf-root .kpi .big { font-size:2rem; font-weight:800; color:#A31621; margin:.3rem 0 .15rem; }
        .t90-pdf-root .kpi .note { font-size:0.78rem; color:#6b7280; }

        /* Legend (tier explanation) */
        .t90-pdf-root .legend-row { display:flex; gap:.85rem; flex-wrap:wrap; margin-top:.6rem; }
        .t90-pdf-root .legend-item { flex:1 1 190px; border-radius:10px; padding:.85rem 1rem; background:#F2F2F2; border-left:5px solid #ccc; }
        .t90-pdf-root .legend-item .lg-title { font-weight:700; font-size:0.85rem; color:#11304F; }
        .t90-pdf-root .legend-item .lg-range { font-size:0.72rem; color:#6b7280; margin-top:.15rem; }
        .t90-pdf-root .legend-item.dev  { border-color:#A31621; }
        .t90-pdf-root .legend-item.emg  { border-color:#F5A623; }
        .t90-pdf-root .legend-item.est  { border-color:#5b7ba8; }
        .t90-pdf-root .legend-item.core { border-color:#1F6B3F; background:#eaf5ee; }

        /* Priority list */
        .t90-pdf-root .priority-list div { font-size:0.96rem; margin-top:.85rem; line-height:1.75; color:#334155; }
        .t90-pdf-root .priority-list b { color:#11304F; }

        /* Readiness donut + sub-scores */
        .t90-pdf-root .readiness-flex { display:flex; gap:2.25rem; align-items:center; flex-wrap:wrap; }
        .t90-pdf-root .donut-wrap { position:relative; width:220px; height:220px; flex:0 0 220px; }
        .t90-pdf-root .donut-center { position:absolute; inset:0; display:flex; flex-direction:column; align-items:center; justify-content:center; }
        .t90-pdf-root .donut-center .pct { font-size:2.1rem; font-weight:800; color:#A31621; }
        .t90-pdf-root .donut-center .lbl { font-size:0.7rem; color:#6b7280; text-transform:uppercase; letter-spacing:.03em; }
        .t90-pdf-root .sub-scores { display:flex; gap:.85rem; flex:1; min-width:260px; }
        .t90-pdf-root .sub-score-card { flex:1; border-radius:12px; padding:1rem; background:#F2F2F2; }
        .t90-pdf-root .sub-score-card .n { font-size:1.65rem; font-weight:800; }
        .t90-pdf-root .sub-score-card .n.mindset { color:#A31621; }
        .t90-pdf-root .sub-score-card .n.skill { color:#F5A623; }
        .t90-pdf-root .sub-score-card .t { font-size:0.75rem; color:#6b7280; margin-top:.25rem; }
        .t90-pdf-root .tag { font-size:0.68rem; font-weight:700; padding:.2rem .55rem; border-radius:.75rem; white-space:nowrap; display:inline-block; margin-top:.4rem; }
        .t90-pdf-root .tag.dev  { background:#fbe6e6; color:#A31621; }
        .t90-pdf-root .tag.emg  { background:#fdf1da; color:#b8730f; }
        .t90-pdf-root .tag.est  { background:#e3eaf5; color:#3a5a8a; }
        .t90-pdf-root .tag.core { background:#dcf0e3; color:#155c31; }

        /* Radar */
        .t90-pdf-root .radar-wrap { display:flex; flex-direction:row; align-items:center; justify-content:center; gap:2rem; }
        .t90-pdf-root .radar-legend { display:flex; flex-direction:column; gap:.6rem; margin-top:.75rem; align-self:flex-start; }
        .t90-pdf-root .radar-legend-item { display:flex; align-items:center; gap:.5rem; font-size:0.95rem; color:#0f2744; font-weight:600; }
        .t90-pdf-root .radar-swatch { width:26px; height:2.5px; border-radius:2px; position:relative; display:inline-block; }
        .t90-pdf-root .radar-swatch::after { content:""; position:absolute; left:50%; top:50%; width:8px; height:8px; border-radius:50%; background:inherit; transform:translate(-50%,-50%); }

        /* Dimension table */
        .t90-pdf-root .dim-table { display:grid; gap:.75rem; }
        .t90-pdf-root .dim-row { display:grid; grid-template-columns:1fr 2fr auto; gap:1rem; align-items:center; padding:.85rem 1rem; border:1px solid #d9e0e7; border-radius:14px; background:#fff; }
        .t90-pdf-root .dim-name { display:flex; flex-direction:column; }
        .t90-pdf-root .dim-name strong { color:#0f2744; font-size:0.92rem; }
        .t90-pdf-root .dim-name span { color:#6b7280; font-size:0.78rem; }
        .t90-pdf-root .dim-bar-wrap { display:flex; align-items:center; gap:.6rem; }
        .t90-pdf-root .dim-bar-track { flex:1; height:10px; background:#e9eef3; border-radius:999px; overflow:hidden; }
        .t90-pdf-root .dim-bar-fill { height:100%; border-radius:999px; background: linear-gradient(90deg, #A81026, #d6a23f); }
        .t90-pdf-root .dim-pct { font-size:0.78rem; font-weight:700; color:#0f2744; width:34px; text-align:right; }
        .t90-pdf-root .dim-chip { font-size:11px; font-weight:700; padding:.25rem .6rem; border-radius:999px; white-space:nowrap; text-align:center; }

        /* Callout */
        .t90-pdf-root .callout { background:linear-gradient(135deg,#fff7f7,#fbeaea); border:1px solid #d9e0e7; border-radius:18px; padding:1.25rem 1.5rem; color:#334155; text-align:justify; }

        /* Back cover */
        .t90-pdf-root .back-cover { min-height:1556px; color:white; }
        .t90-pdf-root .back-cover .page-inner { min-height:1556px; display:flex; flex-direction:column; justify-content:space-between; }
        .t90-pdf-root .closing h2 { margin:0 0 .85rem; font-size:2.8rem; color:#FFFFFF; border-left: 6px solid #FFFFFF !important; padding-left: 1rem; }
        .t90-pdf-root .closing p  { text-align: justify; max-width: 100%; color:rgba(255,255,255,.82); font-size:1.2rem; }
        .t90-pdf-root .contact-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:1rem; margin-top:1.75rem; }
        .t90-pdf-root .contact { background:rgba(163,22,33,.12); border:1px solid rgba(255,255,255,.16); border-radius:16px; padding:.9rem; }
        .t90-pdf-root .contact span   { display:block; color:rgba(255,255,255,.64); font-size:0.73rem; margin-bottom:.25rem; }
        .t90-pdf-root .contact strong { font-size:0.92rem; }
        .t90-pdf-root .small { font-size:0.73rem; color:#5B677A; }
        .t90-pdf-root .light { color:rgba(255,255,255,.66); }
      `}</style>

      <div className="t90-pdf-root">
        <main className="report">

          {/* PAGE 1 — COVER */}
          <section className="t90-page cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
                minHeight: "1556px",
                flexDirection: "column",
              }}
            >
              <div className="logo-panel">
                <svg
                  width="100%"
                  height="100%"
                  viewBox="0 0 500 250"
                  style={{ position: "absolute", top: 0, right: 0, zIndex: 0 }}
                  preserveAspectRatio="none"
                >
                  <path d="M 60,8 L 500,8 L 500,188 L 150,253 Q 120,258 113,229 Z" fill="rgba(0,0,0,0.04)" />
                  <path d="M 60,4 L 500,4 L 500,184 L 150,249 Q 120,254 113,225 Z" fill="rgba(0,0,0,0.06)" />
                  <path d="M 60,0 L 500,0 L 500,180 L 150,245 Q 120,250 113,221 Z" fill="white" />
                </svg>
                <div className="logo-panel-inner">
                  <img
                    src={Acumen_25_Years_LOGO}
                    alt="Acumen Logo"
                    className="logo-img"
                  />
                </div>
              </div>

              <div className="cover-inner">
                <div className="brand">
                  <div className="badge">Confidential Individual Report</div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1, position: "relative", zIndex: 2 }}>
                  <div className="cover-title">
                    <h2>Transition 90</h2>
                    <p>
                      First-Time Manager Assessment — 36 mindset statements and 24 situational skill
                      questions, mapped to 12 mindset dimensions and 12 skill dimensions, to gauge
                      readiness for the transition into a first management role.
                    </p>
                  </div>

                  <div className="cover-grid">
                    <div className="meta"><span>Participant</span><strong>{respondent.name || "—"}</strong></div>
                    <div className="meta"><span>Assessment Date</span><strong>{dateStr}</strong></div>
                    <div className="meta"><span>Overall Readiness</span><strong>{overall.pct}% · {bandInfo(overall.pct).label}</strong></div>
                  </div>

                  <div className="cover-badges">
                    {["Summary", "Overall Readiness", "Mindset Breakdown", "Skill Breakdown", "Understanding Your Profile", "Dimension Radar"].map((b) => (
                      <span key={b} className="cover-badge">{b}</span>
                    ))}
                  </div>
                </div>

                <div className="footer-note" style={{ position: "relative", zIndex: 2 }}>
                  <div>Based on the Transition 90 First-Time Manager Assessment framework.</div>
                  <div>36 mindset items · 24 skill scenarios · 60 questions total</div>
                </div>
              </div>
            </div>
          </section>

          {/* PAGE 2 — ASSESSMENT SUMMARY */}
          <section className="t90-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 2</div>
                  <h2 className="section-title">Assessment Summary</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: "-1rem", marginBottom: "1.25rem" }}>
                What the tiers below mean, and where this participant stands overall.
              </p>

              <div className="card">
                <strong style={{ fontSize: "0.85rem", color: "#11304F" }}>Reading the tiers used throughout this report</strong>
                <div className="legend-row">
                  <div className="legend-item dev">
                    <div className="lg-title">Development Priority</div>
                    <div className="lg-range">0–39% · needs focused coaching first</div>
                  </div>
                  <div className="legend-item emg">
                    <div className="lg-title">Emerging Strength</div>
                    <div className="lg-range">40–59% · present but inconsistent</div>
                  </div>
                  <div className="legend-item est">
                    <div className="lg-title">Established Strength</div>
                    <div className="lg-range">60–79% · reliable, close to reflex</div>
                  </div>
                  <div className="legend-item core">
                    <div className="lg-title">Core Strength</div>
                    <div className="lg-range">80–100% · near-automatic standard</div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: "1rem" }}>
                <div className="readiness-flex">
                  <Donut pct={overall.pct} />
                  <div className="sub-scores">
                    <div className="sub-score-card">
                      <div className="n mindset">{mindset.pct}%</div>
                      <div className="t">Mindset Score<br />36 items · 12 mindsets<br />
                        <span className={`tag ${bandInfo(mindset.pct).level === "strong" ? "core" : bandInfo(mindset.pct).level === "estab" ? "est" : bandInfo(mindset.pct).level === "emerging" ? "emg" : "dev"}`}>{bandInfo(mindset.pct).label}</span>
                      </div>
                    </div>
                    <div className="sub-score-card">
                      <div className="n skill">{skill.pct}%</div>
                      <div className="t">Skill Score<br />24 scenarios · 12 skills<br />
                        <span className={`tag ${bandInfo(skill.pct).level === "strong" ? "core" : bandInfo(skill.pct).level === "estab" ? "est" : bandInfo(skill.pct).level === "emerging" ? "emg" : "dev"}`}>{bandInfo(skill.pct).label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card" style={{ marginTop: "1rem" }}>
                <strong style={{ fontSize: "0.9rem", color: "#A31621", letterSpacing: "0.01em" }}>Your Readiness Snapshot</strong>
                <p className="lead" style={{ marginTop: "0.75rem" }}>{readinessNarrative(overall.pct, mindset.pct, skill.pct)}</p>
              </div>

              <div className="card priority-list" style={{ marginTop: "1rem" }}>
                <strong style={{ fontSize: "0.9rem", color: "#A31621", letterSpacing: "0.01em" }}>Mindsets and Skills to Prioritise</strong>
                {lowestMindset && (
                  <div><b>Mindset to develop:</b> {lowestMindset.label} ({lowestMindset.pct}%) is the lowest mindset score on this report
                    {lowestMindset.pct < 40 ? " — it's in Development Priority, the single highest-leverage conversation to have with this participant." : "."}</div>
                )}
                {lowestSkills.length > 0 && (
                  <div><b>Skills to add:</b> {joinList(lowestSkills.map((d) => `${d.label} (${d.pct}%)`))} {lowestSkills.length === 1 ? "is" : "are"} the lowest skill score{lowestSkills.length === 1 ? "" : "s"} on this report{lowestSkillPct < 40 ? ", in Development Priority" : ""}.</div>
                )}
                {(mindsetCoreStrengths.length > 0 || skillCoreStrengths.length > 0) && (
                  <div><b>Core strengths already in place:</b> {joinList([...mindsetCoreStrengths, ...skillCoreStrengths].map((d) => `${d.label} (${d.pct}%)`))} — no coaching needed here, these are near-automatic.</div>
                )}
                {lowestMindset && linkedRow && linkedShowsMatchingGap && (
                  <div style={{ marginTop: "0.6rem", color: "#6b7280" }}>Note: {lowestMindset.label} and its linked skill dimension are connected — addressing the mindset directly is likely to move the skill score as a side effect. See "The Pattern Behind the Profile" for the full picture.</div>
                )}
              </div>
            </div>
          </section>

          {/* PAGE 3 — OVERALL READINESS + MINDSET BREAKDOWN */}
          <section className="t90-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 3</div>
                  <h2 className="section-title">Mindset Breakdown</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: "-1rem", marginBottom: "1.25rem" }}>
                {mindset.pct}% overall · 12 dimensions · 3 statements each
              </p>

              <div style={{ marginTop: "1.75rem" }}>
                {/* <h3 style={{ color: "#11304F", fontSize: "1.1rem", marginBottom: "0.1rem" }}>Mindset Breakdown</h3> */}
                <p className="lead" style={{ marginBottom: "0.85rem", fontSize: "0.85rem" }}></p>
                {renderDimensionTable(mindset.dimensions)}
                <div className="callout" style={{ marginTop: "1rem" }}>
                  <strong style={{ color: "#A31621" }}>Reading this table:</strong>
                  <p style={{ marginTop: "0.5rem" }}>
                    Each dimension above is scored from 3 statements. Some are worded plainly, so agreeing
                    with them is a good sign. Others are worded as a trap — they describe a limiting belief,
                    so agreeing with them is actually a bad sign (these are the ones marked "reverse-scored").
                    A dimension under 40% means the answers leaned toward the limiting beliefs more often than
                    the healthy ones; 80%+ means they leaned toward the healthy belief almost every time.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* PAGE 4 — SKILL BREAKDOWN */}
          <section className="t90-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 4</div>
                  <h2 className="section-title">Skill Breakdown</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: "-1rem", marginBottom: "1.25rem" }}>
                {skill.pct}% overall · 12 dimensions · 2 scenarios each
              </p>

              {renderDimensionTable(skill.dimensions)}
              <div className="callout" style={{ marginTop: "1rem" }}>
                <strong style={{ color: "#A31621" }}>Reading this table:</strong>
                <p style={{ marginTop: "0.5rem" }}>
                  Each dimension above is tested through two realistic scenarios, and every option in each
                  one was deliberately written to sound reasonable — there's no obviously wrong answer to
                  filter out on tone alone. Instead, every option is weighted on a 4-to-1 maturity ladder
                  against what an experienced manager would actually do. Under 40% means the default
                  instinct consistently reaches for a lower-maturity option; 80%+ means the mature response
                  has become the reflex, not the considered choice.
                </p>
              </div>
            </div>
          </section>

          {/* PAGE 5 — UNDERSTANDING YOUR PROFILE */}
          <section className="t90-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 5</div>
                  <h2 className="section-title">Understanding Your Profile</h2>
                </div>
              </div>

              <div className="card">
                <h3>What This Assessment Measures</h3>
                <p className="lead">
                  Most first-time-manager failures don't come from not knowing what to do — they come from
                  knowing what to do and reverting to old habits anyway, the moment things get busy. That's
                  why this assessment scores two separate things instead of collapsing them into one number.
                </p>
                <p className="lead" style={{ marginTop: "0.85rem" }}>
                  The <strong>Mindset score</strong> measures what a person actually believes about the job
                  — not what they'd say in a training room, but what they agree or disagree with when a
                  statement is specific enough to expose the real belief underneath. This is the layer that
                  decides what a manager reaches for by default when no one is coaching them in the moment.
                </p>
                <p className="lead" style={{ marginTop: "0.85rem" }}>
                  The <strong>Skill score</strong> measures what they'd actually do — tested through
                  realistic, high-pressure scenarios where every option sounds plausible on first read, and
                  only one reflects the response an experienced manager would actually choose, weighted
                  against the alternatives on a 4-to-1 maturity ladder.
                </p>
                <p className="lead" style={{ marginTop: "0.85rem" }}>
                  Read together, the two scores answer a question a single number can't: is a gap a{" "}
                  <strong>belief problem</strong> (knows what's right, doesn't yet believe it under pressure)
                  or an <strong>execution problem</strong> (believes it, hasn't built the muscle to act on it)?
                </p>
              </div>

              <div className="card" style={{ border: "2px solid #A31621", marginTop: "1.5rem" }}>
                <h3 style={{ textTransform: "uppercase", letterSpacing: ".05em", fontSize: "0.9rem" }}>
                  Strengths &amp; Priorities At a Glance
                </h3>
                {(mindsetCoreStrengths.length > 0 || skillCoreStrengths.length > 0) && (
                  <p className="lead">
                    <strong>Core Strengths (80%+):</strong>{" "}
                    {mindsetCoreStrengths.length > 0
                      ? `${joinList(mindsetCoreStrengths.map(coreStrengthLabel))} ${mindsetCoreStrengths.length === 1 ? "is" : "are"} the clearest mindset strength${mindsetCoreStrengths.length === 1 ? "" : "s"} — a high, near-automatic standard is already in place.`
                      : "No mindset dimension has yet reached Core Strength (80%+)."}
                    {skillCoreStrengths.length > 0 && (
                      <> On the skill side, {joinList(skillCoreStrengths.map((d) => d.label))} score{skillCoreStrengths.length === 1 ? "s" : ""} 80%+ — a genuinely strong operational base.</>
                    )}
                  </p>
                )}
                {lowestMindset && (
                  <p className="lead">
                    <strong>Immediate Priority:</strong> {lowestMindset.label} ({lowestMindset.pct}%) is the lowest mindset score on this report
                    {lowestMindset.pct < 40 ? ", placing it in Development Priority" : ""} — worth a direct conversation before anything else.
                  </p>
                )}
                {lowestSkills.length > 0 && (
                  <p className="lead">
                    <strong>Secondary Priorities:</strong> {joinList(lowestSkills.map((d) => d.label))} {lowestSkills.length === 1 ? "is" : "are"}{" "}
                    tied at {lowestSkillPct}% — the lowest skill score{lowestSkills.length === 1 ? "" : "s"} on this report
                    {lowestSkillPct < 40
                      ? ", worth sequencing deliberately rather than tackling at once."
                      : ", though still comfortably above the Development Priority threshold."}
                  </p>
                )}
              </div>
            </div>
          </section>

          {/* PAGE 6 — PATTERN BEHIND THE PROFILE */}
          <section className="t90-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 6</div>
                  <h2 className="section-title">The Pattern Behind the Profile</h2>
                </div>
              </div>

              {lowestMindset && linkedRow && (
                <div className="card" style={{ border: "2px solid #A31621" }}>
                  <p className="lead">
                    {lowestMindset.label} ({lowestMindset.code}, <strong>{lowestMindset.pct}%</strong>) is the lowest mindset score on this report
                    {linkedSkillDims.length > 0 ? (
                      isLinkedAlsoLowestSkill ? (
                        <>
                          , and its linked skill dimension{linkedSkillDims.length === 1 ? "" : "s"} — {joinList(linkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} — {linkedSkillDims.length === 1 ? "is" : "are"} also tied for the lowest skill score on this report.
                        </>
                      ) : linkedBelowMidpoint ? (
                        <>
                          , and its linked skill dimension{linkedSkillDims.length === 1 ? "" : "s"} — {joinList(linkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} — {linkedSkillDims.length === 1 ? "sits" : "sit"} well below the midpoint too, though the single lowest skill score on this report is actually {joinList(lowestSkills.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))}.
                        </>
                      ) : strongLinkedSkillDims.length === linkedSkillDims.length ? (
                        <>
                          . Its linked skill dimension{linkedSkillDims.length === 1 ? "" : "s"} — {joinList(linkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} — {linkedSkillDims.length === 1 ? "actually scores" : "actually score"} well above the midpoint, so this particular gap hasn't shown up as a matching skill weakness yet
                        </>
                      ) : (
                        <>
                          . Of its linked skill dimensions, {joinList(weakLinkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} {weakLinkedSkillDims.length === 1 ? "sits" : "sit"} below the midpoint too, while {joinList(strongLinkedSkillDims.map((d) => `${d.label} (${displayCode(d.code)}, ${d.pct}%)`))} {strongLinkedSkillDims.length === 1 ? "actually scores" : "actually score"} well above it
                        </>
                      )
                    ) : (
                      "."
                    )}{" "}
                    {linkedShowsMatchingGap ? (
                      <>This is very likely one underlying pattern showing up in more than one place, rather than several unrelated development areas: {linkedRow.why.toLowerCase()}.</>
                    ) : (
                      <>That gap is worth naming directly even so, since {linkedRow.why.toLowerCase()} — the belief may simply not have caught up yet with the capability already being demonstrated.</>
                    )}
                  </p>
                  <p className="lead" style={{ marginTop: "0.75rem" }}>
                    {linkedShowsMatchingGap ? (
                      <>
                        A coaching conversation that addresses the mindset directly is likely to move the linked skill score{weakLinkedSkillDims.length === 1 ? "" : "s"} as a side effect —
                        making it the single highest-leverage conversation to have with this participant
                        {secondaryPriorities.length > 0 && (
                          <>, ahead of {joinList(secondaryPriorities.map((d) => d.label))}, which {secondaryPriorities.length === 1 ? "doesn't" : "don't"} share this root cause and can be sequenced separately.</>
                        )}
                      </>
                    ) : (
                      <>
                        A coaching conversation that addresses the mindset directly is unlikely to move an already-strong skill score, but it can help the participant's confidence catch up with evidence they're already producing in practice
                        {secondaryPriorities.length > 0 && (
                          <>. In the meantime, {joinList(secondaryPriorities.map((d) => d.label))} {secondaryPriorities.length === 1 ? "is" : "are"} worth prioritising as {secondaryPriorities.length === 1 ? "a" : ""} genuine skill gap{secondaryPriorities.length === 1 ? "" : "s"} that {secondaryPriorities.length === 1 ? "doesn't" : "don't"} share this root cause.</>
                        )}
                      </>
                    )}
                  </p>
                </div>
              )}

              <h3 style={{ fontSize: "0.9rem", fontWeight: 800, color: "#A31621", textTransform: "uppercase", letterSpacing: ".05em", margin: "1.5rem 0 0.5rem" }}>
                Full Mindset ↔ Skill Map (for reference)
              </h3>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th style={{ background: "#A31621", color: "#fff", textAlign: "left", padding: "0.6rem 0.85rem", fontWeight: 700, fontSize: "0.78rem" }}>Mindset Dimension</th>
                    <th style={{ background: "#A31621", color: "#fff", textAlign: "left", padding: "0.6rem 0.85rem", fontWeight: 700, fontSize: "0.78rem" }}>Related Skill Dimension(s)</th>
                    <th style={{ background: "#A31621", color: "#fff", textAlign: "left", padding: "0.6rem 0.85rem", fontWeight: 700, fontSize: "0.78rem" }}>Why they're linked</th>
                  </tr>
                </thead>
                <tbody>
                  {MINDSET_SKILL_MAP.map((row, i) => (
                    <tr key={row.m} style={i % 2 === 1 ? { background: "#F5F8FC" } : undefined}>
                      <td style={{ padding: "0.6rem 0.85rem", borderBottom: "1px solid #eee", verticalAlign: "top" }}>{row.m} — {row.mLabel}</td>
                      <td style={{ padding: "0.6rem 0.85rem", borderBottom: "1px solid #eee", verticalAlign: "top" }}>{row.sLabel}</td>
                      <td style={{ padding: "0.6rem 0.85rem", borderBottom: "1px solid #eee", verticalAlign: "top" }}>{row.why}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* PAGE 7 — DIMENSION RADAR */}
          <section className="t90-page">
            <div className="page-inner">
              <div className="topbar">
                <div>
                  <div className="section-kicker">Page 7</div>
                  <h2 className="section-title">Dimension Radar</h2>
                </div>
              </div>
              <p className="lead" style={{ marginTop: "-1rem", marginBottom: "1.25rem" }}>
                Mindset and skill scores plotted across all 24 dimensions, on the 0–100% scale.
              </p>

              <div className="card">
                <div className="radar-wrap">
                  <Radar
                    series={[
                      { name: "Mindset", codes: radar.mindset.codes, values: radar.mindset.values, fill: "rgba(163,22,33,.16)", stroke: "#A31621" },
                      { name: "Skill",   codes: radar.skill.codes,   values: radar.skill.values,   fill: "rgba(245,166,35,.20)", stroke: "#F5A623" },
                    ]}
                  />
                  <div className="radar-legend">
                    <div className="radar-legend-item"><span className="radar-swatch" style={{ background: "#A31621" }} /> Mindset</div>
                    <div className="radar-legend-item"><span className="radar-swatch" style={{ background: "#F5A623" }} /> Skill</div>
                  </div>
                </div>
              </div>

              <div className="callout" style={{ marginTop: "1.25rem" }}>
                <strong style={{ color: "#A31621" }}>Reading this chart:</strong>
                <p style={{ marginTop: "0.5rem" }}>
                  Each spoke is one dimension, scaled 0% (centre) to 100% (outer ring). The red shape traces
                  the 12 mindset dimensions (M1–M12); the gold shape traces the 12 skill dimensions, numbered
                  to match the framework. A shape that bulges outward on a spoke marks a strength on that
                  dimension; a shape that pulls toward the centre marks a priority area. Comparing the two
                  shapes side by side is what surfaces belief-vs-execution gaps at a glance — where red runs
                  further out than gold on the same theme, the belief is ahead of the practiced skill.
                </p>
              </div>
            </div>
          </section>

          {/* PAGE 8 — BACK COVER */}
          <section className="t90-page back-cover">
            <div
              style={{
                background: `radial-gradient(circle at top right, rgba(214,162,63,.22), transparent 28%), linear-gradient(135deg,${T.navy},${T.blue} 55%,${T.teal})`,
                minHeight: "1556px",
              }}
            >
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "1.5rem 2.5rem",
                borderBottom: "1px solid rgba(255,255,255,0.15)",
              }}>
                <div style={{ fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: ".12em", fontWeight: 700, color: "rgba(255,255,255,0.66)" }} />
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.5)", letterSpacing: ".05em" }}>
                  Acumen Business Catalyst
                </div>
              </div>

              <div className="page-inner">
                <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", flex: 1 }}>
                  <div className="closing">
                    <h2>The First 90 Days Set the Tone</h2>
                    <p>
                      An overall readiness score of {overall.pct}% reflects where this participant stands
                      today — not a ceiling. Use the mindset and skill breakdowns to prioritise coaching
                      conversations on the dimensions scoring "Emerging" or "Development priority" first.
                    </p>
                  </div>
                </div>

                <div>
                  <div className="card" style={{ marginTop: "1.5rem", background: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.14)", color: "white" }}>
                    <h3 style={{ color: "white" }}>Practical Learning Design</h3>
                    <p className="lead" style={{ color: "rgba(255,255,255,.84)" }}>
                      Prioritise coaching conversations around the lowest-scoring mindset and skill
                      dimensions first. Pair each with a real, current situation the participant is
                      facing so the practice sticks rather than staying theoretical.
                    </p>
                  </div>

                  <div className="contact-grid">
                    <div className="contact"><span>Brand</span><strong>Acumen Business Catalyst</strong></div>
                    <div className="contact"><span>Focus</span><strong>Leadership • Culture • Capability</strong></div>
                    <div className="contact"><span>Report type</span><strong>Transition 90 Assessment Report</strong></div>
                  </div>

                  <p className="light" style={{ marginTop: "1.5rem", fontSize: "0.75rem" }}>
                    This report is generated from the Transition 90 First-Time Manager Assessment framework.
                  </p>
                  <p className="light" style={{ marginTop: "0.35rem", fontSize: "0.7rem", opacity: 0.75 }}>
                    {/* v2 — scoring bands revised to 0–39 / 40–59 / 60–79 / 80–100%. */}
                  </p>
                </div>
              </div>
            </div>
          </section>

        </main>
      </div>
    </div>
  );
}